import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import courierImage0960 from "assets/images/_V6A0960.jpg";
import { OnboardingFooterHostContext } from "features/courier-onboarding/components/OnboardingFooterHostContext";
import LanguageSwitcher from "shared/components/LanguageSwitcher";
import { useLanguage } from "shared/context/LanguageContext";
import { cn } from "shared/lib/utils";

const HAAT_LOGO_URL =
  "https://cdn.prod.website-files.com/6829acf870093a50cc6ceb69/682b03419ead6e45d8f2c59e_logo%20(2).svg";

interface OnboardingSplitLayoutProps {
  progressBar?: React.ReactNode;
  footer?: React.ReactNode;
  scrollKey?: string | number;
  children: React.ReactNode;
}

const OnboardingSplitLayout: React.FC<OnboardingSplitLayoutProps> = ({
  progressBar,
  footer,
  scrollKey,
  children,
}) => {
  const { t } = useTranslation();
  const { dir } = useLanguage();
  const [footerHost, setFooterHost] = useState<HTMLElement | null>(null);
  const mainRef = useRef<HTMLElement>(null);

  useEffect(() => {
    mainRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  }, [scrollKey]);

  return (
    <OnboardingFooterHostContext.Provider value={footerHost}>
      <div
        dir="ltr"
        className="flex h-screen w-full overflow-hidden bg-white"
        style={{ height: "100dvh" }}
      >
        <aside className="relative hidden h-full w-1/2 shrink-0 overflow-hidden bg-gray-900 lg:flex">
          <img
            src={courierImage0960}
            alt=""
            aria-hidden="true"
            className="h-full w-full object-cover object-center"
            loading="eager"
            decoding="async"
          />
        </aside>

        <div className="flex h-full min-w-0 flex-1 flex-col overflow-hidden bg-white">
          <div dir={dir} className="flex h-full w-full flex-col">
            <header
              dir="ltr"
              className="sticky top-0 z-20 flex shrink-0 items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:px-8"
            >
              <img
                src={HAAT_LOGO_URL}
                alt={String(t("appName"))}
                className="h-8 w-auto"
                loading="eager"
                decoding="async"
              />
              <LanguageSwitcher
                variant="header"
                menuAlign="right"
                landingAccent
              />
            </header>

            <main
              ref={mainRef}
              className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 py-6 sm:px-8 sm:py-8"
            >
              {progressBar ? (
                <div className="mb-4 w-full sm:mb-6">{progressBar}</div>
              ) : null}
              {children}
            </main>

            <footer
              ref={setFooterHost}
              className={cn(
                "sticky bottom-0 z-20 shrink-0 border-t border-gray-200 bg-white px-5 pt-4 pb-[calc(1rem_+_env(safe-area-inset-bottom,0px))] sm:px-8",
                !footer && "hidden has-[>*]:block",
              )}
            >
              {footer}
            </footer>
          </div>
        </div>
      </div>
    </OnboardingFooterHostContext.Provider>
  );
};

export default OnboardingSplitLayout;
